import { type HTMLAttributes, type ReactNode, type TdHTMLAttributes, type ThHTMLAttributes } from 'react'

import { cn } from './cn'

/**
 * `.table` from the mockup, wrapped in a horizontal scroller.
 *
 * The wrapper is not decoration: the admin and queue tables are wide, and a
 * table that pushes the page sideways breaks every other column on the screen.
 */
export function Table({ className, children, ...rest }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto">
      <table {...rest} className={cn('table', className)}>
        {children}
      </table>
    </div>
  )
}

export function THead({ className, children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead {...rest} className={className}>
      {children}
    </thead>
  )
}

export function TBody({ className, children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody {...rest} className={className}>
      {children}
    </tbody>
  )
}

export function TR({ className, children, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr {...rest} className={className}>
      {children}
    </tr>
  )
}

export interface CellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Right-aligns the cell — the mockup's convention for row actions. */
  align?: 'left' | 'right' | 'center'
}

export function TH({ align = 'left', className, children, ...rest }: CellProps) {
  return (
    <th
      {...rest}
      className={cn(
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </th>
  )
}

export interface TDProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'right' | 'center'
  /** Muted 12px — the mockup's treatment for scopes, timestamps and notes. */
  meta?: boolean
}

export function TD({ align = 'left', meta = false, className, children, ...rest }: TDProps) {
  return (
    <td
      {...rest}
      className={cn(
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        meta && 'text-meta text-muted',
        className,
      )}
    >
      {children}
    </td>
  )
}

/**
 * The "nothing here" row.
 *
 * A table body that renders zero rows looks identical to one that failed to
 * load, so every table in this app says which it is.
 */
export function TableEmpty({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-2 py-6 text-center text-body text-muted">
        {children}
      </td>
    </tr>
  )
}
